<template>
    <div
        class="product-variation metal-variation"
        @click.prevent.stop="() => {}"
    >
        <span
            v-if="showTitle"
            class="label"
        >
            Metal:
            <span class="selected-value">{{
                selected.primaryMetal.translation.name
            }}</span>
        </span>

        <ul
            class="metal-variations"
            role="listbox"
            aria-label="Metal variations"
        >
            <li
                v-for="(variation, index) in variations"
                :key="index"
                :class="
                    getClassMetalVariation(variation.primaryMetal.systemCode)
                "
                class="variation"
                role="none"
            >
                <ThLink
                    v-qa="
                        variation.primaryMetal.translation.name.replace(
                            'ct',
                            'K'
                        )
                    "
                    :to="getLink(variation)"
                    role="option"
                    :aria-label="
                        getVariationMetalAbbreviation(
                            variation.primaryMetal.systemCode
                        )
                    "
                    :aria-selected="
                        selected.primaryMetal.systemCode ===
                        variation.primaryMetal.systemCode
                    "
                    class="link"
                    :class="getClassLink(variation)"
                    @click.stop.prevent="$emit('click', variation)"
                >
                    {{
                        getVariationMetalAbbreviation(
                            variation.primaryMetal.systemCode
                        )
                    }}
                </ThLink>
            </li>
        </ul>
    </div>
</template>

<script lang="ts" setup generic="T extends MetalVariationFragment">
import { useRoutes } from '@/router/routes'
import type { MetalVariationFragment, ProductLinkFragment } from '@/graphql/api'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import {
    getClassMetalVariation,
    getVariationMetalAbbreviation,
} from '@/views/product/getClassMetalVariation'

import ThLink from '@/components/ui/buttons/ThLink.vue'

const routes = useRoutes()

const props = withDefaults(
    defineProps<{
        selected: T
        variations: (T & ProductLinkFragment)[]
        link?: boolean
        showTitle?: boolean
        /** Overrides the default product link, e.g. to keep a selected stone */
        getRoute?: (variation: T & ProductLinkFragment) => ButtonRouteRelative
    }>(),
    {
        link: true,
        showTitle: true,
        getRoute: undefined,
    }
)

defineEmits<{
    click: [item: T]
}>()

const getLink = (variation: T & ProductLinkFragment) => {
    if (!props.link) {
        return undefined
    }

    return props.getRoute?.(variation) ?? routes.ring.product(variation)
}

const getClassLink = (variation: T) => ({
    'router-link-active':
        props.selected.primaryMetal.systemCode ===
        variation.primaryMetal.systemCode,
})
</script>

<style lang="scss" scoped>
.product-variation {
    padding: 0;
    cursor: default;

    .label {
        display: block;
        font-size: 14px;
        line-height: 1em;
        margin: 0 0 15px;

        .selected-value {
            margin-left: 0.25em;
            font-weight: 700;
        }
    }

    .metal-variations {
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        .variation {
            position: relative;
            width: 28px;
            height: 28px;
            margin: 0 10px 12px 0;
            cursor: pointer;
            font-family: $effra-font-stack;
            text-transform: uppercase;
            font-size: 0.75rem;
            letter-spacing: 0.05em;

            .link {
                width: 100%;
                height: 100%;
                border-radius: 50%;
                background: #c8c8c8;
                display: flex;
                align-items: center;
                justify-content: center;
                color: $slate;
                text-decoration: none;

                &.router-link-active {
                    border: 1px solid $blue;
                    box-shadow: inset 0 0 0 3px $white;
                }
            }

            &.white-gold .link {
                background: #c8c8c8;
            }

            &.yellow-gold .link {
                background: #e7d698;
            }

            &.rose-gold .link {
                background: #efcaad;
            }

            &.platinum .link {
                background: #efefef;
            }
        }
    }
}
</style>
